import React, { useRef, useState } from 'react';
import { Button, Icon, notification } from 'antd';
import styled from 'styled-components';

import { Modal } from '_src/components';
import { PersistentRepo } from '_src/shared/repo';

const PreviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const PreviewItem = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
`;

const PreviewImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  object-fit: cover;
  background: #f5f5f5;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 1px solid #d9d9d9;
  border-radius: 50%;
  background: #fff;
  color: #595959;
  cursor: pointer;
  line-height: 1;
`;

const CameraPreview = styled.video`
  width: 100%;
  max-width: 560px;
  background: #000;
`;

const CameraPreviewContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const getPreviewUrl = ({ image, imageUrl, token }) => {
  if (!image) {
    return '';
  }

  if (typeof image === 'object') {
    if (image.file) {
      return image.file.indexOf('data:image') === 0
        ? image.file
        : `data:image/jpeg;base64,${image.file}`;
    }

    if (image.url || image.src) {
      return image.url || image.src;
    }
  }

  if (typeof image === 'string') {
    if (
      image.indexOf('data:image') === 0 ||
      image.indexOf('blob:') === 0 ||
      image.indexOf('http') === 0
    ) {
      return image;
    }

    if (imageUrl) {
      return `${GLOBALS.BASE_API}/${imageUrl}/${image}/${token}`;
    }

    return `data:image/jpeg;base64,${image}`;
  }

  return '';
};

const readFileAsDataUrl = file =>
  new Promise(resolve => {
    const reader = new FileReader();

    reader.onload = e => resolve(e.target && e.target.result);
    reader.onerror = () => resolve('');
    reader.readAsDataURL(file);
  });

const getBase64Payload = image => {
  if (typeof image !== 'string') {
    return '';
  }

  const parts = image.split(',');

  return parts.length > 1 ? parts[1] : '';
};

const getImageItem = image => ({
  file: getBase64Payload(image)
});

const getDataUrlSize = image => {
  const payload = getBase64Payload(image);

  return payload ? Math.ceil((payload.length * 3) / 4) : 0;
};

const imageExtensionPattern = /\.(png|jpe?g|webp|gif|heic|heif)$/i;

const isFileAccepted = (file, accept) => {
  const fileName = file.name || '';
  const fileType = file.type || '';
  const acceptedTypes = accept
    .split(',')
    .map(type => type.trim().toLowerCase())
    .filter(Boolean);

  if (!acceptedTypes.length) {
    return true;
  }

  return acceptedTypes.some(type => {
    if (type.charAt(0) === '.') {
      return fileName.toLowerCase().endsWith(type);
    }

    if (type.endsWith('/*')) {
      const baseType = type.replace('*', '');

      return (
        fileType.indexOf(baseType) === 0 ||
        (baseType === 'image/' && imageExtensionPattern.test(fileName))
      );
    }

    return fileType.toLowerCase() === type;
  });
};

const formatFileSize = size => {
  if (!size) {
    return '0 MB';
  }

  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const isMobileOrTabletBrowser = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  const hasTouchMac = /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1;

  return /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent) || hasTouchMac;
};

export default ({
  value = [],
  label = 'Add images',
  onChange = () => {},
  onFilePicked,
  imageUrl = '',
  buttonStyles,
  loading,
  buttonLabel = 'Add images',
  accept = 'image/*',
  maxSize,
  multiple = true,
  dataCy = '',
  useCamera = true,
  cameraButtonLabel = 'Take photo'
}) => {
  const filePicker = useRef(null);
  const cameraPicker = useRef(null);
  const videoRef = useRef(null);
  const [isCameraShown, setCameraVisibility] = useState(false);
  const [videoStream, setVideoStream] = useState();
  const token = PersistentRepo.get('token');
  const images = Array.isArray(value) ? value : value ? [value] : [];
  const showCameraPicker = useCamera;

  const stopCameraStream = stream => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const closeCamera = () => {
    stopCameraStream(videoStream);
    setVideoStream(undefined);
    setCameraVisibility(false);
  };

  const addPickedImages = pickedImageItems => {
    try {
      onChange(multiple ? [...images, ...pickedImageItems] : pickedImageItems[0]);
    } catch (error) {}

    if (onFilePicked) {
      const pickedPayloads = pickedImageItems.map(image => image.file);

      onFilePicked(multiple ? pickedPayloads : pickedPayloads[0]);
    }
  };

  const removeImage = index => {
    const nextImages = images.filter((image, i) => i !== index);

    try {
      onChange(multiple ? nextImages : undefined);
    } catch (error) {}
  };

  const handleFilesPicked = async e => {
    const input = e.target;
    const pickedFiles = Array.from(input.files || []);
    const oversizedFiles = maxSize ? pickedFiles.filter(file => file.size > maxSize) : [];
    const unsupportedFiles = pickedFiles.filter(file => !isFileAccepted(file, accept));

    if (oversizedFiles.length) {
      notification.warning({
        message: 'Image size limit exceeded',
        description: `Each image must be ${formatFileSize(maxSize)} or smaller.`
      });
    }

    if (unsupportedFiles.length) {
      notification.warning({
        message: 'Unsupported image type',
        description: `Only files matching ${accept} are allowed.`
      });
    }

    const files = pickedFiles.filter(file => {
      return file.size <= (maxSize || Infinity) && isFileAccepted(file, accept);
    });

    if (!files.length) {
      input.value = '';
      return;
    }

    const pickedImages = (await Promise.all(files.map(readFileAsDataUrl))).filter(Boolean);
    const pickedImageItems = pickedImages.map(getImageItem).filter(image => image.file);

    if (!pickedImageItems.length) {
      input.value = '';
      return;
    }

    addPickedImages(pickedImageItems);

    input.value = '';
  };

  const openCamera = () => {
    if (isMobileOrTabletBrowser()) {
      if (cameraPicker.current) {
        cameraPicker.current.click();
      }
      return;
    }

    if (
      typeof navigator !== 'undefined' &&
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia
    ) {
      setCameraVisibility(true);
      navigator.mediaDevices
        .getUserMedia({ video: { facingMode: { ideal: 'environment' } } })
        .then(stream => {
          if (!videoRef.current) {
            stopCameraStream(stream);
            return;
          }

          videoRef.current.srcObject = stream;
          videoRef.current.play();
          setVideoStream(stream);
        })
        .catch(() => {
          setCameraVisibility(false);
          notification.error({ message: 'Unable to access camera' });
        });
      return;
    }

    if (cameraPicker.current) {
      cameraPicker.current.click();
    }
  };

  const takePhoto = () => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    const canvas = document.createElement('canvas');

    canvas.width = video.videoWidth || video.width;
    canvas.height = video.videoHeight || video.height;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);

    const snapshot = canvas.toDataURL('image/jpeg');
    const snapshotItem = getImageItem(snapshot);

    closeCamera();

    if (maxSize && getDataUrlSize(snapshot) > maxSize) {
      notification.warning({
        message: 'Image size limit exceeded',
        description: `Each image must be ${formatFileSize(maxSize)} or smaller.`
      });
      return;
    }

    if (!snapshotItem.file) {
      return;
    }

    addPickedImages([snapshotItem]);
  };

  return (
    <>
      <div>{label}</div>

      <Button
        onClick={() => filePicker.current.click()}
        style={buttonStyles}
        loading={loading}
        data-cy={dataCy}
      >
        {loading ? (
          <></>
        ) : (
          <>
            <Icon type="picture" style={{ margin: '0 5px 0 0' }} />
            {buttonLabel}
          </>
        )}
      </Button>

      {showCameraPicker && (
        <Button
          onClick={openCamera}
          style={{ marginLeft: 8, ...buttonStyles }}
          loading={loading}
          data-cy={dataCy ? `${dataCy}-camera` : ''}
        >
          {loading ? (
            <></>
          ) : (
            <>
              <Icon type="camera" style={{ margin: '0 5px 0 0' }} />
              {cameraButtonLabel}
            </>
          )}
        </Button>
      )}

      <input
        ref={filePicker}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={handleFilesPicked}
      />

      {showCameraPicker && (
        <input
          ref={cameraPicker}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFilesPicked}
        />
      )}

      <Modal
        visible={isCameraShown}
        title={cameraButtonLabel}
        onCancel={closeCamera}
        okText="Use photo"
        onOk={takePhoto}
      >
        <CameraPreviewContainer>
          <CameraPreview ref={videoRef} width="560" height="420" autoPlay playsInline />
        </CameraPreviewContainer>
      </Modal>

      {!!images.length && (
        <PreviewRow>
          {images.map((image, i) => {
            const previewUrl = getPreviewUrl({ image, imageUrl, token });

            return previewUrl ? (
              <PreviewItem key={i}>
                <PreviewImage src={previewUrl} alt="" />
                <RemoveButton type="button" onClick={() => removeImage(i)}>
                  <Icon type="close" style={{ fontSize: '10px' }} />
                </RemoveButton>
              </PreviewItem>
            ) : null;
          })}
        </PreviewRow>
      )}
    </>
  );
};
